import React, { useState } from 'react';
import './EnvironmentGenerator.css';
import { influencerService, getInfluencerImageUrl } from '../services/influencerApi';

const timeOfDayOptions = [
  { value: 'golden-hour', label: 'Golden Hour' },
  { value: 'morning', label: 'Morning' },
  { value: 'noon', label: 'Noon' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'night', label: 'Night' },
];

const weatherOptions = [
  { value: 'clear', label: 'Clear' },
  { value: 'cloudy', label: 'Cloudy' },
  { value: 'rainy', label: 'Rainy' },
  { value: 'foggy', label: 'Foggy' },
  { value: 'snowy', label: 'Snowy' },
];

const cameraOptions = [
  { value: 'wide', label: 'Wide' },
  { value: 'medium', label: 'Medium' },
  { value: 'close-up', label: 'Close-up' },
  { value: 'extreme-close-up', label: 'Extreme Close-up' },
  { value: 'bird-eye', label: "Bird's Eye" },
];

const lightingStyleOptions = [
  { value: 'soft-natural', label: 'Soft Natural' },
  { value: 'dramatic', label: 'Dramatic' },
  { value: 'neon', label: 'Neon' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'moody', label: 'Moody' },
];

const styleOptions = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'stylized', label: 'Stylized' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'anime', label: 'Anime' },
  { value: 'illustration', label: 'Illustration' },
];

function EnvironmentGenerator() {
  const [location, setLocation] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('golden-hour');
  const [weather, setWeather] = useState('clear');
  const [mood, setMood] = useState('');
  const [camera, setCamera] = useState('wide');
  const [lighting, setLighting] = useState('soft-natural');
  const [style, setStyle] = useState('photorealistic');
  const [colorPalette, setColorPalette] = useState('');
  const [inlineEdits, setInlineEdits] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState(null);
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [environmentName, setEnvironmentName] = useState('');

  const buildPromptFromParams = () => {
    const parts = [];
    if (location) parts.push(location);
    if (mood) parts.push(`${mood} mood`);
    parts.push(`${timeOfDay.replace('-', ' ')} lighting`);
    parts.push(`${weather} weather`);
    parts.push(`${camera} angle shot`);
    parts.push(`${lighting.replace('-', ' ')} lighting style`);
    parts.push(`${style} style`);
    if (colorPalette) parts.push(`color palette: ${colorPalette}`);
    if (additionalDetails) parts.push(additionalDetails);
    return parts.join(', ');
  };

  const handleGenerate = async () => {
    if (!location.trim()) {
      setError('Please enter a location');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');
      const prompt = buildPromptFromParams();
      const response = await influencerService.generateBackground(prompt, 1024, 720);

      if (response.success) {
        setGeneratedImage(getInfluencerImageUrl(response.path));
        setCurrentImagePath(response.path);
        setEnhancedPrompt(response.enhanced_prompt);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate environment');
      console.error('Generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApplyEdit = async () => {
    if (!inlineEdits.trim() || !currentImagePath) {
      setError('Please enter edit instructions and generate an environment first');
      return;
    }

    try {
      setIsEditing(true);
      setError('');

      const result = await influencerService.editImage(
        currentImagePath.replace('/public/', 'public/'),
        inlineEdits.trim(),
        0.7
      );

      setGeneratedImage(getInfluencerImageUrl(result.path));
      setCurrentImagePath(result.path);
      setInlineEdits('');
    } catch (err) {
      setError(err.message || 'Failed to apply edits');
      console.error('Edit error:', err);
    } finally {
      setIsEditing(false);
    }
  };

  const handleSaveEnvironment = () => {
    if (!currentImagePath) {
      setError('No environment to save');
      return;
    }
    setShowNameDialog(true);
  };

  const handleConfirmSave = async () => {
    if (!environmentName.trim() || !currentImagePath) return;

    try {
      setIsSaving(true);
      setError('');
      const filename = currentImagePath.split('/').pop() || '';
      const result = await influencerService.saveEnvironment(filename, environmentName.trim());

      setShowNameDialog(false);
      setEnvironmentName('');
      alert(`Environment "${result.name}" saved successfully!`);
      handleReset();
    } catch (err) {
      setError(err.message || 'Failed to save environment');
      console.error('Save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setLocation('');
    setTimeOfDay('golden-hour');
    setWeather('clear');
    setMood('');
    setCamera('wide');
    setLighting('soft-natural');
    setStyle('photorealistic');
    setColorPalette('');
    setInlineEdits('');
    setAdditionalDetails('');
    setGeneratedImage(null);
    setCurrentImagePath(null);
    setEnhancedPrompt('');
    setError('');
    setEnvironmentName('');
  };

  return (
    <div className="environment-generator">
      <div className="header">
        <h1>Build Your Environment</h1>
        <p className="subtitle">Describe the scene — we handle the complexity</p>
      </div>

      <div className="env-layout">
        {/* Left - Form */}
        <div className="env-form-panel">
          <div className="form-card">
            {/* Scene Basics */}
            <div className="form-section">
              <h3 className="section-title">Scene Basics</h3>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Misty cedar forest, cyberpunk alley..."
                />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Time of Day</label>
                  <select value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}>
                    {timeOfDayOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Weather</label>
                  <select value={weather} onChange={(e) => setWeather(e.target.value)}>
                    {weatherOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Mood</label>
                <input
                  type="text"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  placeholder="Serene, ominous, cinematic..."
                />
              </div>
            </div>

            {/* Visual Feel */}
            <div className="form-section">
              <h3 className="section-title">Visual Feel</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Camera</label>
                  <select value={camera} onChange={(e) => setCamera(e.target.value)}>
                    {cameraOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Lighting</label>
                  <select value={lighting} onChange={(e) => setLighting(e.target.value)}>
                    {lightingStyleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Style</label>
                  <select value={style} onChange={(e) => setStyle(e.target.value)}>
                    {styleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Color Palette</label>
                  <input
                    type="text"
                    value={colorPalette}
                    onChange={(e) => setColorPalette(e.target.value)}
                    placeholder="teal, amber, cool blue"
                  />
                </div>
              </div>
            </div>

            {/* Advanced */}
            <button
              className="advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <i className={`fas fa-chevron-right ${showAdvanced ? 'rotated' : ''}`}></i>
              Advanced Scene Details (Optional)
            </button>

            {showAdvanced && (
              <div className="form-section">
                <div className="form-group">
                  <label>Additional Details</label>
                  <textarea
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    placeholder="Prop placements, specific textures, architectural details..."
                    rows="2"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="message error">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {enhancedPrompt && (
              <div className="enhanced-prompt">
                <strong>Enhanced Prompt:</strong>
                <p>{enhancedPrompt}</p>
              </div>
            )}

            {/* Actions */}
            <div className="form-actions">
              <button
                className="xbtn xbtn-md xbtn-secondary"
                onClick={handleReset}
                disabled={isGenerating || isEditing || isSaving}
              >
                Reset
              </button>
              <button
                className="xbtn xbtn-md xbtn-primary"
                onClick={handleGenerate}
                disabled={isGenerating || isEditing || isSaving || !location.trim()}
              >
                {isGenerating ? (
                  <><i className="fas fa-spinner fa-spin"></i> Generating...</>
                ) : (
                  <><i className="fas fa-magic"></i> Generate Environment</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right - Preview */}
        <div className="env-preview-panel">
          <div className="env-preview-image">
            {isGenerating ? (
              <div className="preview-placeholder">
                <i className="fas fa-spinner fa-spin fa-3x"></i>
                <p>Generating your environment...</p>
                <small>This may take 60-90 seconds</small>
              </div>
            ) : generatedImage ? (
              <img src={generatedImage} alt="Generated environment" />
            ) : (
              <div className="preview-placeholder">
                <i className="fas fa-mountain fa-3x"></i>
                <p>Environment preview will appear here</p>
                <small>Fill in the form and click Generate</small>
              </div>
            )}
          </div>

          {/* Inline Edits */}
          <div className="inline-edit-section">
            <div className="form-group">
              <label>Make Inline Edits</label>
              <input
                type="text"
                value={inlineEdits}
                onChange={(e) => setInlineEdits(e.target.value)}
                placeholder='e.g., "Replace the chair on the left with a pink throne"'
                disabled={isEditing || !generatedImage}
              />
            </div>
          </div>

          <div className="env-preview-actions">
            <button
              className="xbtn xbtn-md xbtn-secondary"
              onClick={handleApplyEdit}
              disabled={isEditing || !inlineEdits.trim() || !generatedImage || isSaving}
            >
              {isEditing ? (
                <><i className="fas fa-spinner fa-spin"></i> Applying...</>
              ) : (
                'Apply Edit'
              )}
            </button>
            <button
              className="xbtn xbtn-md xbtn-primary"
              onClick={handleSaveEnvironment}
              disabled={!generatedImage || isGenerating || isEditing || isSaving}
            >
              {isSaving ? (
                <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              ) : (
                <><i className="fas fa-check"></i> Save Environment</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Name Dialog */}
      {showNameDialog && (
        <div className="modal-overlay" onClick={() => setShowNameDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Name Your Environment</h3>
            <p className="modal-desc">Give your environment a descriptive name to save it to your library.</p>
            <div className="modal-field">
              <label>Name</label>
              <input
                type="text"
                value={environmentName}
                onChange={(e) => setEnvironmentName(e.target.value)}
                placeholder="e.g., Golden Hour Bus Scene"
                onKeyPress={(e) => e.key === 'Enter' && handleConfirmSave()}
              />
            </div>
            <div className="modal-actions">
              <button className="xbtn xbtn-md xbtn-secondary" onClick={() => setShowNameDialog(false)}>Cancel</button>
              <button
                className="xbtn xbtn-md xbtn-primary"
                onClick={handleConfirmSave}
                disabled={!environmentName.trim() || isSaving}
              >
                {isSaving ? (
                  <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                ) : (
                  'Save Environment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnvironmentGenerator;
